import React, { useState } from "react";
import { CheckCircle2Icon, Loader2Icon, MailIcon, MessageCircleIcon, PhoneIcon } from "lucide-react";
import { PapelPicado } from "../components/culture/PapelPicado";
import { useSite } from "../context/SiteContext";
import { eventInquiryContact, eventTypeOptions } from "../data/eventInquiry";
type InquiryKind = "catering" | "private";
type Status = "idle" | "sending" | "sent" | "error";
type FormValues = {
  name: string;
  phone: string;
  email: string;
  eventType: string;
  location: string;
  date: string;
  time: string;
  guests: string;
  message: string;
};
const emptyForm: FormValues = {
  name: "",
  phone: "",
  email: "",
  eventType: "",
  location: "Victor",
  date: "",
  time: "",
  guests: "",
  message: ""
};
export function InquiryPage({
  kind
}: {
  kind: InquiryKind;
}) {
  const {
    t
  } = useSite();
  const isCatering = kind === "catering";
  const [values, setValues] = useState<FormValues>({
    ...emptyForm,
    eventType: isCatering ? "Catering" : "Private Event"
  });
  const [errors, setErrors] = useState<Partial<Record<keyof FormValues, string>>>({});
  const [status, setStatus] = useState<Status>("idle");
  const update = (field: keyof FormValues) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setValues(current => ({
      ...current,
      [field]: event.target.value
    }));
    setErrors(current => ({
      ...current,
      [field]: undefined
    }));
  };
  const summary = buildSummary(values);
  const whatsappHref = `https://wa.me/${eventInquiryContact.whatsappNumber}?text=${encodeURIComponent(summary)}`;
  const smsHref = `${eventInquiryContact.smsHref}?&body=${encodeURIComponent(summary)}`;
  const mailHref = `mailto:${eventInquiryContact.email}?subject=${encodeURIComponent(`Event inquiry — ${values.name}`)}&body=${encodeURIComponent(summary)}`;
  const validate = () => {
    const next: Partial<Record<keyof FormValues, string>> = {};
    if (values.name.trim().length < 2) next.name = t("Please enter your name.", "Escribe tu nombre.");
    if (values.phone.replace(/\D/g, "").length < 10) next.phone = t("Enter a valid phone number.", "Escribe un teléfono válido.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) next.email = t("Enter a valid email.", "Escribe un correo válido.");
    if (!values.date) next.date = t("Choose a date.", "Elige una fecha.");
    if (values.message.trim().length < 10) next.message = t("Tell us a little about your event.", "Cuéntanos un poco sobre tu evento.");
    setErrors(next);
    return Object.keys(next).length === 0;
  };
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validate()) return;
    setStatus("sending");
    try {
      const response = await fetch(eventInquiryContact.endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json"
        },
        body: JSON.stringify({
          _subject: `New ${values.eventType} inquiry — ${values.name} (${values.date})`,
          _replyto: values.email,
          _template: "table",
          _captcha: "false",
          Name: values.name,
          Phone: values.phone,
          Email: values.email,
          "Event type": values.eventType,
          Location: values.location,
          Date: values.date,
          Time: values.time || "—",
          Guests: values.guests || "—",
          Message: values.message,
          "Events phone": eventInquiryContact.phone
        })
      });
      if (!response.ok) throw new Error("Request failed");
      setStatus("sent");
    } catch {
      setStatus("error");
    }
  };
  const fieldClass = (field: keyof FormValues) => `min-h-12 w-full rounded-xl border bg-white px-4 py-3 text-base text-agavedeep outline-none transition-[border-color,box-shadow] duration-150 placeholder:text-agavedeep/40 focus:ring-2 focus:ring-marigold/40 ${errors[field] ? "border-chili" : "border-agavedeep/20 focus:border-marigold"}`;
  return <section className="bg-cream text-agavedeep" data-id="element-532" data-mp="lpm5qtvstdbd">
      <PapelPicado size="sm" data-id="element-533" data-mp="lob34xe3ablu" />
      <div className="mx-auto grid max-w-7xl gap-10 px-5 pb-24 pt-10 sm:px-8 lg:grid-cols-[.85fr_1.15fr] lg:gap-16 lg:pt-16" data-id="element-534" data-mp="gs2snxau76l0">
        <div data-id="element-535" data-mp="uho48mmb4jj0">
          <h1 className="font-serif text-[44px] font-bold leading-[0.95] sm:text-6xl" data-id="element-536" data-mp="ui67w4bau00y">
            {t(isCatering ? "Plan Your Event" : "Host Your Celebration", isCatering ? "Planea tu evento" : "Celebra con nosotros")}
          </h1>
          <p className="mt-5 text-lg leading-8 text-agavedeep/80" data-id="element-537" data-mp="3ty2w87k17ui7">
            {t("Weddings, receptions, birthdays, corporate events, family gatherings and catering. Tell us what you are celebrating and our events team will reply personally.", "Bodas, recepciones, cumpleaños, eventos corporativos, reuniones familiares y catering. Cuéntanos qué celebras y nuestro equipo de eventos te responderá personalmente.")}
          </p>
          <div className="mt-8 space-y-3 border-t border-agavedeep/15 pt-6" data-id="element-538" data-mp="1i8a801pa3hn7">
            <a href={eventInquiryContact.phoneHref} className="flex min-h-11 items-center gap-3 font-semibold hover:text-chili" data-id="element-539" data-mp="1qorh7zspqp16">
              <PhoneIcon className="h-5 w-5 text-marigold" aria-hidden="true" data-id="element-540" data-mp="1up1n7cgi9fg1" />
              {eventInquiryContact.phone}
            </a>
            <a href={`mailto:${eventInquiryContact.email}`} className="flex min-h-11 items-center gap-3 break-all font-semibold hover:text-chili" data-id="element-541" data-mp="1lpy0zzm1kjxb">
              <MailIcon className="h-5 w-5 flex-none text-marigold" aria-hidden="true" data-id="element-542" data-mp="29gty9c99o7by" />
              {eventInquiryContact.email}
            </a>
          </div>
        </div>

        {status === "sent" ? <div role="status" className="flex flex-col items-center justify-center rounded-3xl bg-agavedeep px-6 py-14 text-center text-cream sm:px-12" data-id="element-543" data-mp="1zo4p4o16v5js">
            <CheckCircle2Icon className="h-12 w-12 text-marigold" aria-hidden="true" data-id="element-544" data-mp="2semujvpdxj0d" />
            <h2 className="mt-5 font-serif text-4xl font-bold" data-id="element-545" data-mp="2x64v4inhxhkt">{t("¡Gracias! Your request was sent.", "¡Gracias! Tu solicitud fue enviada.")}</h2>
            <p className="mt-4 max-w-md leading-7 text-cream/80" data-id="element-546" data-mp="370tvuvdez7w7">
              {t(`Our events team received your details at ${eventInquiryContact.email} and will contact you soon. Want a faster reply? Send the same details by text.`, `Nuestro equipo recibió tus datos en ${eventInquiryContact.email} y te contactará pronto. ¿Quieres respuesta más rápida? Envía los mismos datos por mensaje.`)}
            </p>
            <div className="mt-8 grid w-full max-w-sm gap-3" data-id="element-547" data-mp="2jbr3ash8rumx">
              <a href={whatsappHref} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-marigold font-bold text-agavedeep" data-id="element-548" data-mp="2ja7qxcypi3qf">
                <MessageCircleIcon className="h-4 w-4" aria-hidden="true" data-id="element-549" data-mp="29c09kt0i9jnw" />
                {t("Send via WhatsApp", "Enviar por WhatsApp")}
              </a>
              <a href={smsHref} className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full border border-cream/40 font-bold" data-id="element-550" data-mp="20ehib6w9q93k">
                {t("Send by text message", "Enviar por mensaje de texto")}
              </a>
            </div>
          </div> : <form noValidate onSubmit={submit} className="rounded-3xl border border-agavedeep/10 bg-white/70 p-5 shadow-sm sm:p-8" data-id="element-551" data-mp="owvh2k3af3k1">
            <div className="grid gap-5 sm:grid-cols-2" data-id="element-552" data-mp="tu40eo069dmv">
              <Field label={t("Full name", "Nombre completo")} error={errors.name} className="sm:col-span-2" data-id="element-553" data-mp="3tatkyya8mf80">
                <input autoComplete="name" value={values.name} onChange={update("name")} className={fieldClass("name")} data-id="element-554" data-mp="179j1zyniudm" />
              </Field>
              <Field label={t("Phone", "Teléfono")} error={errors.phone} data-id="element-555" data-mp="6381z817nk6n">
                <input type="tel" inputMode="tel" autoComplete="tel" value={values.phone} onChange={update("phone")} className={fieldClass("phone")} data-id="element-556" data-mp="axm0vkf309g2" />
              </Field>
              <Field label={t("Email", "Correo")} error={errors.email} data-id="element-557" data-mp="3ekckbh5fftc6">
                <input type="email" inputMode="email" autoComplete="email" value={values.email} onChange={update("email")} className={fieldClass("email")} data-id="element-558" data-mp="27xjz0wuldj0i" />
              </Field>
              <Field label={t("Type of event", "Tipo de evento")} data-id="element-559" data-mp="230the5352fcz">
                <select value={values.eventType} onChange={update("eventType")} className={fieldClass("eventType")} data-id="element-560" data-mp="2gz9ss9by4u1g">
                  {eventTypeOptions.map(([english, spanish]) => <option key={english} value={english} data-id="element-561" data-mp="2qvmiyx8a8prg">{t(english, spanish)}</option>)}
                </select>
              </Field>
              <Field label={t("Location", "Ubicación")} data-id="element-562" data-mp="2lvqlzhy05yle">
                <select value={values.location} onChange={update("location")} className={fieldClass("location")} data-id="element-563" data-mp="30l64kandqxq0">
                  <option value="Victor" data-id="element-564" data-mp="2vo65wksv0x8x">Blue Agave — Victor</option>
                  <option value="Driggs" data-id="element-565" data-mp="39o76dz57q2nv">Agave Family — Driggs</option>
                  <option value="Off-site / Catering" data-id="element-566" data-mp="34ochu28wtp5z">{t("Off-site / Catering", "Fuera del restaurante / Catering")}</option>
                </select>
              </Field>
              <Field label={t("Date", "Fecha")} error={errors.date} data-id="element-567" data-mp="3o7rrkasac3pv">
                <input type="date" value={values.date} onChange={update("date")} className={fieldClass("date")} data-id="element-568" data-mp="3t7mhab0bqn9c" />
              </Field>
              <Field label={t("Time", "Hora")} data-id="element-569" data-mp="3fbisix87z749">
                <input type="time" value={values.time} onChange={update("time")} className={fieldClass("time")} data-id="element-570" data-mp="3j9qcs21w6qk7" />
              </Field>
              <Field label={t("Number of guests", "Número de invitados")} className="sm:col-span-2" data-id="element-571" data-mp="35ha3ijiedwau">
                <input type="number" inputMode="numeric" min={1} value={values.guests} onChange={update("guests")} className={fieldClass("guests")} data-id="element-572" data-mp="3ac678gwzy83s" />
              </Field>
              <Field label={t("Tell us about your celebration", "Cuéntanos sobre tu celebración")} error={errors.message} className="sm:col-span-2" data-id="element-573" data-mp="30l1vrr6j55dp">
                <textarea rows={5} value={values.message} onChange={update("message")} className={`${fieldClass("message")} min-h-[140px]`} data-id="element-574" data-mp="2rjlse3e4u3iz" />
              </Field>
            </div>

            {status === "error" && <div role="alert" className="mt-6 rounded-2xl border border-chili/30 bg-chili/5 p-4 text-sm leading-6" data-id="element-575" data-mp="17e943law0vj6">
                <p className="font-bold text-chili" data-id="element-576" data-mp="tezy1egebhsx">{t("We couldn't send it right now.", "No pudimos enviarlo en este momento.")}</p>
                <p className="mt-1" data-id="element-577" data-mp="yf5hw8gfc00g">{t("Your details are saved — send them directly instead:", "Tus datos siguen aquí — envíalos directamente:")}</p>
                <div className="mt-3 flex flex-wrap gap-2" data-id="element-578" data-mp="1q4h0zlnzupqx">
                  <a href={mailHref} className="inline-flex min-h-10 items-center rounded-full bg-agavedeep px-4 font-bold text-cream" data-id="element-579" data-mp="1q61wpem1oeqd">{t("Email", "Correo")}</a>
                  <a href={whatsappHref} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-10 items-center rounded-full bg-marigold px-4 font-bold text-agavedeep" data-id="element-580" data-mp="1ham5l5cocdoh">WhatsApp</a>
                  <a href={smsHref} className="inline-flex min-h-10 items-center rounded-full border border-agavedeep/30 px-4 font-bold" data-id="element-581" data-mp="29szwekzleze2">{t("Text", "Mensaje")}</a>
                </div>
              </div>}

            <button type="submit" disabled={status === "sending"} className="mt-7 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-full bg-agavedeep font-bold text-cream transition-[background-color,transform] duration-150 hover:bg-[#0B2C22] active:scale-[0.98] disabled:opacity-70 sm:w-auto sm:px-10" data-id="element-582" data-mp="2f56c75i8u01v">
              {status === "sending" && <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-583" data-mp="1w8di57194v6h" />}
              {status === "sending" ? t("Sending…", "Enviando…") : t("Send Event Request", "Enviar solicitud")}
            </button>
          </form>}
      </div>
    </section>;
}
function Field({
  label,
  error,
  className = "",
  children
}: {
  label: string;
  error?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return <label className={`flex flex-col gap-2 text-sm font-bold ${className}`} data-id="element-584" data-mp="3onmv3ixyhu5y">
      {label}
      {children}
      {error && <span className="text-xs font-semibold text-chili" data-id="element-585" data-mp="3snnktzqi9rsb">{error}</span>}
    </label>;
}
function buildSummary(values: FormValues) {
  return ["Event inquiry — Blue Agave", `Name: ${values.name}`, `Phone: ${values.phone}`, `Email: ${values.email}`, `Event: ${values.eventType} (${values.location})`, `Date: ${values.date} ${values.time}`.trim(), `Guests: ${values.guests || "—"}`, `Message: ${values.message}`].join("\n");
}